import { ChevronLeft, ChevronRight, Upload, X } from 'lucide-react'
import Card, { SectionLabel } from '@/components/ui/Card'
import { useToast } from '@/components/ui/toast'
import { MAX_IMAGES, type Draft, type GalleryItem, type PatchDraft } from './draft'

/** The gallery: the first picture is the cover, new picks upload on save. */
export default function ImagesSection({ draft, patch }: { draft: Draft; patch: PatchDraft }) {
  const toast = useToast()

  const addImages = (files: FileList | null) => {
    if (!files?.length) return
    const room = MAX_IMAGES - draft.images.length
    if (files.length > room) {
      toast.error('Too many images', `A product can carry at most ${MAX_IMAGES} images.`)
    }
    const added: GalleryItem[] = Array.from(files)
      .slice(0, Math.max(0, room))
      .map((file) => ({
        kind: 'new',
        localId: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
        file,
        previewUrl: URL.createObjectURL(file),
      }))
    if (added.length > 0) patch({ images: [...draft.images, ...added] })
  }

  const removeImage = (index: number) => {
    const item = draft.images[index]
    // A new pick never reached the server, so dropping it is purely local.
    // An existing row is only really deleted when Save sends the array
    // without it - Cancel never destroys anything.
    if (item.kind === 'new') URL.revokeObjectURL(item.previewUrl)
    patch({ images: draft.images.filter((_, i) => i !== index) })
  }

  const moveImage = (index: number, delta: -1 | 1) => {
    const target = index + delta
    if (target < 0 || target >= draft.images.length) return
    const next = [...draft.images]
    ;[next[index], next[target]] = [next[target], next[index]]
    patch({ images: next })
  }

  return (
    <Card>
      <SectionLabel>Product images</SectionLabel>
      <div className="flex flex-wrap items-start gap-3">
        {draft.images.map((item, index) => (
          <div key={item.kind === 'existing' ? `e-${item.id}` : item.localId} className="relative">
            <img
              src={item.kind === 'existing' ? item.image_path : item.previewUrl}
              alt=""
              className="size-28 rounded-lg border border-foreground/10 object-cover"
              onError={(e) => ((e.target as HTMLImageElement).style.opacity = '0.3')}
            />
            {index === 0 && (
              <span className="absolute left-1 top-1 rounded bg-primary px-1 py-px text-2xs font-bold text-white">Cover</span>
            )}
            <button
              type="button"
              aria-label="Remove image"
              onClick={() => removeImage(index)}
              className="absolute -right-1.5 -top-1.5 flex size-5 items-center justify-center rounded-full border border-foreground/20 bg-background text-foreground/60 shadow-sm hover:text-danger"
            >
              <X className="size-3" />
            </button>
            <div className="mt-1 flex justify-center gap-1">
              <button
                type="button"
                aria-label="Move image left"
                onClick={() => moveImage(index, -1)}
                disabled={index === 0}
                className="flex size-8 items-center justify-center rounded border border-foreground/15 text-foreground/60 hover:bg-foreground/5 disabled:opacity-30 sm:size-6"
              >
                <ChevronLeft className="size-3" />
              </button>
              <button
                type="button"
                aria-label="Move image right"
                onClick={() => moveImage(index, 1)}
                disabled={index === draft.images.length - 1}
                className="flex size-8 items-center justify-center rounded border border-foreground/15 text-foreground/60 hover:bg-foreground/5 disabled:opacity-30 sm:size-6"
              >
                <ChevronRight className="size-3" />
              </button>
            </div>
          </div>
        ))}
        {draft.images.length < MAX_IMAGES && (
          <label className="flex size-28 cursor-pointer flex-col items-center justify-center gap-1 rounded-lg border-2 border-dashed border-foreground/20 text-foreground/50 hover:border-primary/50 hover:text-primary">
            <Upload className="size-4" />
            <span className="text-2xs font-semibold">Add images</span>
            <input
              type="file"
              multiple
              accept="image/jpeg,image/png,image/webp"
              className="hidden"
              onChange={(e) => {
                addImages(e.target.files)
                e.target.value = ''
              }}
            />
          </label>
        )}
      </div>
      <p className="mt-2 text-2xs text-muted">
        First image is the cover shown on cards. JPG, PNG or WEBP, max 4 MB each, up to {MAX_IMAGES} images. Uploads
        happen when you save.
      </p>
    </Card>
  )
}
